<script>
import {
    showModal
} from '@app/store/modalStore.svelte.js';
import {
    projectState
} from '@app/store/projectStore.svelte.js';
import {
    eventGroupState, isSelectedEventGroupLocked
} from '@app/store/eventGroupStore.svelte.js';
import { toast } from '@zerodevx/svelte-toast';
import { canEditMetadata } from '@app/store/permissionStore.svelte.js';
import {
    commitHistory
} from '@app/store/historyStore.svelte.js';
import {
    editorState
} from "@app/store/editorStore.svelte.js";
import insertEv from '@cclib/functions/eventGroups/insertEvent.js';
import insertColorByLine from '@cclib/functions/quill/insertColorByLine.js';
import { current } from 'immer';
import * as m from '@app/paraglide/messages.js';
import Menu from "../menu/Menu.svelte";
import MenuItem from "../menu/MenuItem.svelte";
import MenuDivider from "../menu/MenuDivider.svelte";

let isLocked = $derived(isSelectedEventGroupLocked());

function guardedShowModal(modalName) {
    if (isSelectedEventGroupLocked()) {
        toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
        return;
    }
    showModal(modalName);
}

function syncQuillFromEvent(ev) {
    if (!window.quillEditor || !ev) {
        return;
    }
    let delta = window.quillEditor.clipboard.convert({ html: ev.text });
    window.quillEditor.setContents(delta, "silent");
    window.quillEditor.formatLine(0, 9999, 'align', ev.alignment === 'left' ? '' : ev.alignment, "silent");
}

function insertEvent(above = false) {
    if (isSelectedEventGroupLocked()) {
        toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
        return;
    }

    const egIdx = projectState.selected;
    let eventOptions;
    let eventIndex = eventGroupState[egIdx].selected.length > 0 ? eventGroupState[egIdx].selected[0] : null;
    eventIndex = above ? eventIndex : eventIndex !== null ? eventIndex + 1 : null;
    
    if (eventGroupState[egIdx].events.length > 0){
        try {
            let eventTemplate = eventGroupState[egIdx].selected.length > 0 ? eventGroupState[egIdx].events[eventGroupState[egIdx].selected[0]] : eventGroupState[egIdx].events[eventGroupState[egIdx].events.length-1];

            eventOptions = {
                alignment : eventTemplate.alignment, 
                xPos : eventTemplate.xPos,
                yPos : eventTemplate.yPos,
                writingMode : eventTemplate.writingMode,
                voice : eventTemplate.voice,
                speakingStyle : eventTemplate.speakingStyle,
                style : eventTemplate.style,
                rate : eventTemplate.rate,
                track : eventTemplate.track ?? false,
                start: editorState.insertStartOnInsert ? player.currentTime : false,
                end: editorState.insertStartOnInsert ? player.currentTime + eventGroupState[egIdx].minDuration : false,
            }     
        } catch(err){
            console.log(err);
        }
    }

    commitHistory({
        name: "insert event",
        eventGroup: egIdx,
        mutate: {
            eventGroupState: (draft) => {
                draft[egIdx] = insertEv(
                    current(draft[egIdx]),
                    eventOptions,
                    eventIndex,
                );
                draft[egIdx].selected =
                    eventIndex !== null
                        ? [eventIndex]
                        : [draft[egIdx].events.length - 1];
            },
        },
    });
}

function insertBlock() {
    if (window.quillEditor === undefined){
        return;
    }

    if (isSelectedEventGroupLocked()) {
        toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
        return;
    }

    const egIdx = projectState.selected;
    const selectedIdx = eventGroupState[egIdx]?.selected[0];
    if (selectedIdx === undefined) {
        return;
    }
    
    let range = window?.quillEditor?.getSelection();
    if (!range) {
        return;
    }

    window.quillEditor.insertText(range.index, "█", "silent");
    const newText = window.quillEditor.root.innerHTML;

    commitHistory({
        name: "insert block",
        eventGroup: egIdx,
        mutate: {
            eventGroupState: (draft) => {
                draft[egIdx].events[selectedIdx].text = newText;
            },
        },
    });
}

function insertEllipsis() {
    if (!window.quillEditor){
        return;
    }

    if (isSelectedEventGroupLocked()) {
        toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
        return;
    }

    try {
        const egIdx = projectState.selected;
        const selectedIdx = eventGroupState[egIdx]?.selected[0];
        if (selectedIdx === undefined) {
            return;
        }

        let range = window.quillEditor?.getSelection();
        if (!range) {
            return;
        }

        window.quillEditor.insertText(range.index, "…", "silent");
        const newText = window.quillEditor.root.innerHTML;

        commitHistory({
            name: "insert ellipsis",
            eventGroup: egIdx,
            mutate: {
                eventGroupState: (draft) => {
                    draft[egIdx].events[selectedIdx].text = newText;
                },
            },
        });
    } catch (err){
        console.log(err.message);
    }    
}

//For each selected event, if a speaker is assigned to the event, update the event text to include span tags with the speaker color set to match.
function insertSpeakerColor(){
    if (eventGroupState.length === 0 || projectState.selected === null || eventGroupState[projectState.selected].selected.length === 0){
        return;
    }

    if (isSelectedEventGroupLocked()) {
        toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
        return;
    }

    const egIdx = projectState.selected;
    const selected = [...eventGroupState[egIdx].selected];

    commitHistory({
        name: "insert speaker color",
        eventGroup: egIdx,
        mutate: {
            eventGroupState: (draft) => {
                selected.forEach((evIndex) => {
                    let ev = draft[egIdx].events[evIndex];
                    if (ev.speakers.length === 0){
                        return;
                    }

                    if (ev.speakers.length === 1){
                        let speaker = ev.speakers[0];
                        ev.text = insertColorByLine(ev.text, speaker.colour);
                    } else {
                        const lineCount = ev.text.split("</p>").length;
                        for (let lineIndex = 0; lineIndex < lineCount; lineIndex++) {
                            let speaker = ev.speakers[lineIndex] || ev.speakers[0];
                            ev.text = insertColorByLine(ev.text, speaker.colour, lineIndex);
                        }
                    }
                });
            },
        },
    });

    const firstSelected = selected[0];
    if (firstSelected !== undefined) {
        syncQuillFromEvent(eventGroupState[egIdx].events[firstSelected]);
    }
}

</script>

<Menu id="insert" label={m.insert()} ariaLabel={m.insert_menu()}>
    <MenuItem
        label={m.event_before()}
        icon="bi-arrow-up-circle"
        id="insertEventBefore"
        shortcut="Insert Event (Above)"
        disabled={isLocked}
        disabledReason={m.event_group_locked_toast()}
        onSelect={() => insertEvent(true)} />
    <MenuItem
        label={m.event_after()}
        icon="bi-arrow-down-circle"
        id="insertEventAfter"
        shortcut="Insert Event (Below)"
        disabled={isLocked}
        disabledReason={m.event_group_locked_toast()}
        onSelect={() => insertEvent(false)} />
    <MenuDivider />
    <MenuItem
        label={m.full_block()}
        icon="bi-square-fill"
        id="insertBlock"
        disabled={isLocked}
        disabledReason={m.event_group_locked_toast()}
        onSelect={insertBlock} />
    <MenuItem
        label={m.ellipsis_char()}
        id="insertEllipsis"
        shortcut="Insert Ellipsis"
        disabled={isLocked}
        disabledReason={m.event_group_locked_toast()}
        onSelect={insertEllipsis} />
    <MenuItem
        label={m.music_notes()}
        icon="bi-music-note"
        id="insertMusicNotes"
        shortcut="Insert ♪"
        disabled={isLocked}
        disabledReason={m.event_group_locked_toast()}
        onSelect={() => guardedShowModal("musicNotes")} />
    {#if canEditMetadata("speakers")}
    <MenuItem
        label={m.speaker_name()}
        icon="bi-person"
        id="insertSpeaker"
        shortcut="Insert Speaker"
        disabled={isLocked}
        disabledReason={m.event_group_locked_toast()}
        onSelect={() => guardedShowModal("insertSpeaker")} />
    <MenuItem
        label={m.speaker_color()}
        icon="bi-palette"
        id="insertSpeakerColor"
        disabled={isLocked}
        disabledReason={m.event_group_locked_toast()}
        onSelect={insertSpeakerColor} />
    {/if}
    <MenuDivider />
    <MenuItem
        label={m.blank_frames()}
        icon="bi-film"
        id="insertBlankFrames"
        disabled={isLocked}
        disabledReason={m.event_group_locked_toast()}
        onSelect={() => guardedShowModal("blankFrames")} />
</Menu>
